import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Plus, 
  Edit, 
  Trash2,
  Save,
  X,
  ArrowLeft,
  Star,
  Search,
  Filter,
  MessageSquare
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    project_type: '',
    is_featured: ''
  });
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_title: '',
    client_company: '',
    client_location: '',
    testimonial_text: '',
    rating: 5,
    project_type: 'Sport',
    is_featured: false,
    is_active: true
  });

  const projectTypes = [
    'Sport',
    'Corporate',
    'Mariage',
    'Événement',
    'Drone & FPV',
    'Autre'
  ];

  useEffect(() => {
    loadTestimonials();
  }, [filters]);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.project_type) params.append('project_type', filters.project_type);
      if (filters.is_featured !== '') params.append('is_featured', filters.is_featured);
      
      const response = await axios.get(`${API}/admin/testimonials?${params}`);
      
      if (response.data && response.data.success) {
        let testimonialList = response.data.data;
        
        // Client-side search filtering
        if (filters.search) {
          testimonialList = testimonialList.filter(testimonial => 
            testimonial.client_name.toLowerCase().includes(filters.search.toLowerCase()) ||
            testimonial.testimonial_text.toLowerCase().includes(filters.search.toLowerCase()) ||
            (testimonial.client_company && testimonial.client_company.toLowerCase().includes(filters.search.toLowerCase()))
          );
        }
        
        setTestimonials(testimonialList);
      }
    } catch (error) {
      console.error('Failed to load testimonials:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les avis",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonial.id}`, formData);
        toast({
          title: "Succès",
          description: "Avis modifié avec succès",
        });
      } else {
        await axios.post(`${API}/admin/testimonials`, formData);
        toast({
          title: "Succès",
          description: "Avis ajouté avec succès",
        });
      }
      
      resetForm();
      loadTestimonials();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'enregistrement",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      client_name: testimonial.client_name,
      client_title: testimonial.client_title || '',
      client_company: testimonial.client_company || '',
      client_location: testimonial.client_location || '',
      testimonial_text: testimonial.testimonial_text,
      rating: testimonial.rating,
      project_type: testimonial.project_type || 'Sport',
      is_featured: testimonial.is_featured,
      is_active: testimonial.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`);
      toast({
        title: "Succès",
        description: "Avis supprimé avec succès",
      });
      loadTestimonials();
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_title: '',
      client_company: '',
      client_location: '',
      testimonial_text: '',
      rating: 5,
      project_type: 'Sport',
      is_featured: false,
      is_active: true
    });
    setEditingTestimonial(null);
    setShowForm(false);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <button
              onClick={resetForm}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à la liste
            </button>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingTestimonial ? 'Modifier l\'avis' : 'Ajouter un avis'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom complet du client"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fonction/Titre
                  </label>
                  <input
                    type="text"
                    value={formData.client_title}
                    onChange={(e) => setFormData({...formData, client_title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Président du club"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise/Organisation
                  </label>
                  <input
                    type="text"
                    value={formData.client_company}
                    onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom de l'entreprise ou club"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Localisation
                  </label>
                  <input
                    type="text"
                    value={formData.client_location}
                    onChange={(e) => setFormData({...formData, client_location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ville, région"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de projet
                  </label>
                  <select
                    value={formData.project_type}
                    onChange={(e) => setFormData({...formData, project_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note *
                  </label>
                  <select
                    required
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <option key={rating} value={rating}>
                        {rating} étoile{rating > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Témoignage *
                  </label>
                  <textarea
                    required
                    rows="4"
                    value={formData.testimonial_text}
                    onChange={(e) => setFormData({...formData, testimonial_text: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Le témoignage complet du client..."
                  />
                </div>

                <div className="md:col-span-2 space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Avis mis en avant (affiché en priorité)</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Avis actif (visible sur le site)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                  style={{ backgroundColor: '#f97316' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingTestimonial ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <button
              onClick={() => window.location.href = '/admin'}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </button>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Gestion des Avis</h1>
              <p className="text-gray-600">Gérez les témoignages de vos clients</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
              style={{ backgroundColor: '#f97316' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter Avis
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rechercher
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Client, entreprise ou témoignage..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de projet
              </label>
              <select
                value={filters.project_type}
                onChange={(e) => handleFilterChange('project_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les types</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.is_featured}
                onChange={(e) => handleFilterChange('is_featured', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les statuts</option>
                <option value="true">Mis en avant</option>
                <option value="false">Standard</option>
              </select>
            </div>
          </div>
        </div>

        {/* Testimonials List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#f97316' }}></div>
              <p className="text-gray-600">Chargement des avis...</p>
            </div>
          ) : (
            <>
              {testimonials.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun avis</h3>
                  <p className="text-gray-600 mb-4">Commencez par ajouter votre premier témoignage client</p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                    style={{ backgroundColor: '#f97316' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un avis
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="p-6 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <div className="flex mr-3">
                              {renderStars(testimonial.rating)}
                            </div>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 mr-2">
                              {testimonial.project_type}
                            </span>
                            {testimonial.is_featured && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-2">
                                Mis en avant
                              </span>
                            )}
                            {!testimonial.is_active && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Inactive
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-1">
                            {testimonial.client_name}
                            {testimonial.client_title && (
                              <span className="text-sm font-normal text-gray-600 ml-2">
                                • {testimonial.client_title}
                              </span>
                            )}
                          </h3>
                          {testimonial.client_company && (
                            <p className="text-sm text-gray-600 mb-2">
                              {testimonial.client_company}
                              {testimonial.client_location && ` • ${testimonial.client_location}`}
                            </p>
                          )}
                          <p className="text-gray-700 mb-2 line-clamp-3">
                            "{testimonial.testimonial_text}"
                          </p>
                          <div className="text-sm text-gray-500">
                            Créé le {new Date(testimonial.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEdit(testimonial)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(testimonial.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;